














































































































































































































import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import { ActuatorType } from '@/models/actuatorType';
import MachineRepository from '@/api/MachineRepository';
import { isExcludedReportMachine } from '@/misc/reportCustomerFilter';
import { buildActuatorColorMap, getActuatorColor, getActuatorColors } from '@/misc/actuatorChartColors';

const ActuatorTypeStore = namespace('actuatorType');

interface ActuatorLineChartDataset {
  label: string;
  data: number[];
  color?: string;
}

interface ActuatorMonthlySeries {
  actuator: string;
  data: number[];
}

interface ActuatorMonthlyYearData {
  months: string[];
  series: ActuatorMonthlySeries[];
}

interface ActuatorStatEntry {
  actuator: string;
  unit: string;
  count: number;
}

interface ActuatorModelLocation {
  customerId: string;
  customerName: string;
  stationId: string;
  stationName: string;
  count: number;
}

interface ActuatorModelKpi {
  name: string;
  count: number;
}

interface ActuatorTypeKpi {
  name: string;
  totalCount: number;
  models: ActuatorModelKpi[];
}

@Component({
  components: {
    ReportsNav: () => import('@/components/ReportsNav.component.vue'),
    ReportsPageHeader: () => import('@/components/ReportsPageHeader.component.vue'),
    BarChart: () => import('@/components/charts/BarChart.component.vue'),
    PieChart: () => import('@/components/charts/PieChart.component.vue'),
    LineChart: () => import('@/components/charts/LineChart.component.vue'),
  },
})
export default class ReportsLabelsView extends Vue {
  private reportsRepository = RepositoryFactory.get('reports');
  private machineRepository: MachineRepository = RepositoryFactory.get('machine');

  @ActuatorTypeStore.Action('loadAllActuatorTypes')
  public loadAllActuatorTypes!: () => Promise<ActuatorType[]>;

  @ActuatorTypeStore.Getter('actuatorTypes')
  public actuatorTypes!: ActuatorType[];

  public years = [2021, 2022, 2023, 2024, 2025];
  public selectedYearIndex = this.years.length - 1;
  public selectedActuatorYearIndex = this.years.length - 1;

  public get selectedChartYear(): number {
    return this.years[this.selectedYearIndex] ?? this.years[this.years.length - 1];
  }

  public chartData: Record<number, { label: string; count: number }[]> = {};
  public actuatorMonthlyData: Record<number, ActuatorMonthlyYearData> = {};
  public stats: any = {};
  public actuatorTypeKpis: ActuatorTypeKpi[] = [];
  public actuatorStatsLoading = true;
  public actuatorMonthlyLoading = true;
  public openActuatorPanel: number | null = null;
  public expandedModels: string[] = [];
  public machinesByActuator: Record<string, any[]> = {};
  public loadingActuatorMachines: Record<string, boolean> = {};

  public get actuatorPieChartData(): { name: string; count: number }[] {
    return this.actuatorTypeKpis
      .filter((type) => type.totalCount > 0)
      .map((type) => ({ name: type.name, count: type.totalCount }))
      .sort((a, b) => b.count - a.count);
  }

  public get actuatorColorMap(): Map<string, string> {
    const names = [
      ...this.actuatorTypes.map((type) => type.name),
      ...this.actuatorTypeKpis.map((type) => type.name),
    ];

    Object.values(this.actuatorMonthlyData).forEach((yearData) => {
      yearData.series?.forEach((entry) => names.push(entry.actuator));
    });

    return buildActuatorColorMap(names);
  }

  public get actuatorPieLabels(): string[] {
    return this.actuatorPieChartData.map((entry) => entry.name);
  }

  public get actuatorPieValues(): number[] {
    return this.actuatorPieChartData.map((entry) => entry.count);
  }

  public get actuatorPieColors(): string[] {
    return getActuatorColors(this.actuatorPieLabels, this.actuatorColorMap);
  }

  public getActuatorLineLabels(year: number): string[] {
    return this.actuatorMonthlyData[year]?.months ?? [];
  }

  public getActuatorLineDatasets(year: number): ActuatorLineChartDataset[] {
    const colorMap = this.actuatorColorMap;
    const series = this.actuatorMonthlyData[year]?.series ?? [];

    return series.map((entry) => ({
      label: entry.actuator,
      data: this.toCumulativeSeries(entry.data),
      color: getActuatorColor(entry.actuator, colorMap),
    }));
  }

  private toCumulativeSeries(monthlyValues: number[]): number[] {
    let runningTotal = 0;
    return monthlyValues.map((value) => {
      runningTotal += value;
      return runningTotal;
    });
  }

  /** Returns label strings for the chart of a given year */
  public getLabels(year: number): string[] {
    const data = this.chartData[year];
    if (!data) return [];
    return data.slice(0, 50).map((d) => d.label);
  }

  /** Returns count values for the chart of a given year */
  public getValues(year: number): number[] {
    const data = this.chartData[year];
    if (!data) return [];
    return data.slice(0, 50).map((d) => d.count);
  }

  public modelKey(typeName: string, modelName: string): string {
    return `${typeName}::${modelName}`;
  }

  public isModelExpanded(typeName: string, modelName: string): boolean {
    return this.expandedModels.includes(this.modelKey(typeName, modelName));
  }

  public toggleModel(typeName: string, modelName: string): void {
    const key = this.modelKey(typeName, modelName);
    if (this.isModelExpanded(typeName, modelName)) {
      this.expandedModels = this.expandedModels.filter((item) => item !== key);
      return;
    }

    this.expandedModels = [...this.expandedModels, key];
    this.ensureActuatorMachinesLoaded(typeName);
  }

  public isActuatorMachinesLoading(typeName: string): boolean {
    return !!this.loadingActuatorMachines[typeName.toLowerCase()];
  }

  public stationRoute(location: ActuatorModelLocation) {
    return {
      name: 'station_details_view',
      params: {
        customerId: location.customerId,
        stationId: location.stationId,
      },
    };
  }

  public getModelLocations(typeName: string, modelName: string): ActuatorModelLocation[] {
    const machines = this.machinesByActuator[typeName.toLowerCase()] || [];
    const stationMap = new Map<string, ActuatorModelLocation>();

    machines
      .filter((machine) => machine.actuatorUnit === modelName && !isExcludedReportMachine(machine))
      .forEach((machine) => {
        const station = machine.station;
        if (!station) return;

        const stationId = String(station.id ?? station._id ?? '');
        if (!stationId) return;

        const customer = typeof station.customer === 'object' ? station.customer : null;
        const customerId = customer?.id ? String(customer.id) : '';
        const customerName = customer?.name || '–';
        const stationName = station.name || stationId;

        if (stationMap.has(stationId)) {
          stationMap.get(stationId)!.count += 1;
          return;
        }

        stationMap.set(stationId, {
          customerId,
          customerName,
          stationId,
          stationName,
          count: 1,
        });
      });

    return [...stationMap.values()].sort((a, b) => {
      const customerCompare = a.customerName.localeCompare(b.customerName, 'de');
      if (customerCompare !== 0) return customerCompare;
      return a.stationName.localeCompare(b.stationName, 'de');
    });
  }

  public onActuatorPanelChange(panelIndex: number | null): void {
    if (panelIndex === null || panelIndex === undefined) return;
    const type = this.actuatorTypeKpis[panelIndex];
    if (!type) return;
    this.ensureActuatorMachinesLoaded(type.name);
  }

  private getActuatorApiName(typeName: string): string {
    const matchedType = this.actuatorTypes.find(
      (type) => type.name.toLowerCase() === typeName.toLowerCase(),
    );
    return matchedType?.lowerCaseName || typeName.toLowerCase();
  }

  public async ensureActuatorMachinesLoaded(typeName: string): Promise<void> {
    const key = typeName.toLowerCase();
    if (this.machinesByActuator[key] || this.loadingActuatorMachines[key]) return;

    this.$set(this.loadingActuatorMachines, key, true);
    try {
      const response = await this.machineRepository.getMachinesByActuatorName(this.getActuatorApiName(typeName));
      const machines = (response.data ?? []).filter(
        (machine: { station?: { customer?: { name?: string } | null } | null }) => !isExcludedReportMachine(machine),
      );
      this.$set(this.machinesByActuator, key, machines);
    } catch {
      this.$set(this.machinesByActuator, key, []);
    } finally {
      this.$set(this.loadingActuatorMachines, key, false);
    }
  }

  private parseActuatorStats(raw: any): ActuatorStatEntry[] {
    if (!raw) return [];

    if (Array.isArray(raw)) {
      return raw.map((item) => ({
        actuator: String(item.actuator || item.actuatorType || item.actuatorName || 'Unbekannt'),
        unit: String(item.actuatorUnit || item.unit || item.label || '–'),
        count: Number(item.count ?? item.machineCount ?? 0),
      }));
    }

    if (typeof raw === 'object') {
      return Object.entries(raw).flatMap(([actuator, models]) => {
        if (!Array.isArray(models)) return [];
        return models.map((item: any) => ({
          actuator: String(actuator),
          unit: String(item.actuatorUnit || item.unit || item.label || '–'),
          count: Number(item.count ?? item.machineCount ?? 0),
        }));
      });
    }

    return [];
  }

  private buildActuatorTypeKpis(entries: ActuatorStatEntry[], actuatorTypes: ActuatorType[]): ActuatorTypeKpi[] {
    const typeMap = new Map<string, ActuatorTypeKpi>();

    actuatorTypes.forEach((type) => {
      typeMap.set(type.name.toLowerCase(), {
        name: type.name,
        totalCount: 0,
        models: [],
      });
    });

    entries.forEach(({ actuator, unit, count }) => {
      const key = actuator.toLowerCase();
      if (!typeMap.has(key)) {
        typeMap.set(key, { name: actuator, totalCount: 0, models: [] });
      }

      const type = typeMap.get(key)!;
      type.totalCount += count;

      const existingModel = type.models.find((model) => model.name === unit);
      if (existingModel) {
        existingModel.count += count;
      } else {
        type.models.push({ name: unit, count });
      }
    });

    return [...typeMap.values()]
      .map((type) => ({
        ...type,
        models: type.models.sort((a, b) => b.count - a.count),
      }))
      .sort((a, b) => {
        if (b.totalCount !== a.totalCount) return b.totalCount - a.totalCount;
        return a.name.localeCompare(b.name, 'de');
      });
  }

  public async mounted() {
    try {
      const [byYear, labelStats, actuatorStats, actuatorTypes, actuatorMonthly] = await Promise.allSettled([
        this.reportsRepository.getLabelsByYear(),
        this.reportsRepository.getLabelStats(),
        this.reportsRepository.getActuatorStats(),
        this.loadAllActuatorTypes(),
        this.reportsRepository.getActuatorTypesByMonth(),
      ]);

      if (byYear.status === 'fulfilled') {
        this.chartData = byYear.value.data ?? {};
      }
      if (labelStats.status === 'fulfilled') {
        this.stats = labelStats.value.data ?? {};
      }
      if (actuatorMonthly.status === 'fulfilled') {
        this.actuatorMonthlyData = actuatorMonthly.value.data ?? {};
      }
      if (actuatorStats.status === 'fulfilled' || actuatorTypes.status === 'fulfilled') {
        const entries = actuatorStats.status === 'fulfilled'
          ? this.parseActuatorStats(actuatorStats.value.data)
          : [];
        const types = actuatorTypes.status === 'fulfilled' ? actuatorTypes.value : [];
        this.actuatorTypeKpis = this.buildActuatorTypeKpis(entries, types);
      }
    } catch {
      // keep defaults
    } finally {
      this.actuatorStatsLoading = false;
      this.actuatorMonthlyLoading = false;
    }
  }
}
